import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { imageEntry, buildSrcSet, largestVariant } from '../utils/images';

interface ResponsiveImageProps {
  /** Original public path, e.g. "/img/Kueche/foo.jpg". */
  src: string;
  alt: string;
  /** Responsive `sizes` hint. Defaults to full viewport width. */
  sizes?: string;
  /**
   * When set (e.g. "4 / 3" or 1.33), the image is cropped to this ratio with
   * object-fit: cover — used for uniform cards and avatars. When omitted, the
   * image keeps its intrinsic aspect ratio.
   */
  aspectRatio?: string | number;
  /** Above-the-fold images should pass eager to skip lazy-loading. */
  loading?: 'lazy' | 'eager';
  /** Styles applied to the <img>. */
  sx?: SxProps<Theme>;
}

/**
 * Renders a <picture> that serves generated WebP variants (with a JPEG/PNG
 * fallback) via srcSet/sizes, lazy-loads by default, and always reserves layout
 * space (explicit width/height or an aspect-ratio box) so images never shift the
 * page as they load. Falls back to a plain <img> for unoptimized sources.
 */
const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  alt,
  sizes = '100vw',
  aspectRatio,
  loading = 'lazy',
  sx,
}) => {
  const entry = imageEntry(src);
  const cover = aspectRatio != null;

  const imgSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    height: cover ? '100%' : 'auto',
    objectFit: cover ? 'cover' : undefined,
    ...sx,
  };

  // Unoptimized source: still lazy-load and reserve space if we know the ratio.
  if (!entry) {
    return (
      <Box sx={cover ? { aspectRatio: String(aspectRatio), width: '100%' } : undefined}>
        <Box component="img" src={src} alt={alt} loading={loading} sx={imgSx} />
      </Box>
    );
  }

  const img = (
    <Box
      component="img"
      src={largestVariant(entry, entry.ext)}
      srcSet={buildSrcSet(entry, entry.ext)}
      sizes={sizes}
      alt={alt}
      loading={loading}
      width={entry.width}
      height={entry.height}
      sx={imgSx}
    />
  );

  return (
    <Box
      component="picture"
      sx={{ display: 'block', ...(cover ? { aspectRatio: String(aspectRatio), width: '100%' } : {}) }}
    >
      <source type="image/webp" srcSet={buildSrcSet(entry, 'webp')} sizes={sizes} />
      {img}
    </Box>
  );
};

export default ResponsiveImage;
